import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';

import { UIService } from '@services';
import {
  AuthStore,
  getDevicePlatformBasingOnRoles,
  hasMainDeviceRole,
  InstallationDetailsActions,
  InstallationPackageStatus,
  InstallationsActions,
  InstallationsStore,
  Platform,
  RemoteConfigStore,
  RemoteServiceActions,
  RouterStore,
} from '@store';

import * as Api from '@vi-iot/client';
import {
  DashboardDevice,
  DashboardInstallationData,
  DashboardMainDevice,
  DeviceRoles,
  MonetizationApi,
  PackageSubscriptionPayload,
  PaidFeatureScope,
  PurchasesApi,
  SubscriptionPlan,
  SubscriptionsDetails,
  SubscriptionWithPaidFeatures,
  SupportedFeaturesName,
  UpdateBookingSubscriptionResponse,
  ViError,
} from '@vi-iot/client';
import { ViToastService } from '@vi-iot/ui';
import { combineLatest, forkJoin, Observable, of, zip } from 'rxjs';
import {
  catchError,
  filter,
  finalize,
  map,
  mergeMap,
  retry,
  startWith,
  switchMap,
  take,
  tap,
  throttleTime,
  timeout,
} from 'rxjs/operators';

import {
  AppInsightsMonitoringService,
  LogLevelEnum,
} from 'src/app/shared/services/app-insights/app-insights-monitoring.service';
import { APP_INSIGHTS_FEATURES } from 'src/app/shared/services/app-insights/events/features.events';
import { MonetizationApiPicker } from '../../../projects/client/apis/monetization/monetization.api-picker';
import { BookingActions } from './booking.actions';
import {
  BOOKING_PARAMS,
  BOOKING_TRIAL_STATUS,
  BookingDashboardDevice,
  BookingQueryParams,
  BookingSubscription,
  ContingentSubscriptionsQueryParams,
  PartialBookingDashboardDevice,
  Whitelist,
} from './booking.model';
import { BookingService } from './booking.service';
import { BookingStore } from './booking.store';
import { HighestSubscriptionPlanPipe } from '../../+kanban-board/kanban-installation-list/pipes/highest-subscription-plan/highest-subscription-plan.pipe';

export const noInstallationsError = 'No Installations';

@Injectable()
export class BookingEffects {
  loadBookingInstallations$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        BookingActions.loadBookingInstallations.attempt,
        BookingActions.loadBookingInstallations.retry
      ),
      mergeMap((action) =>
        // We need all installations initially to know number of bookable elements in paginator
        this.getAllInstallations().pipe(
          concatLatestFrom(() => this.routerStore.selectQueryParams$),
          map(([installations, queryParams]) => {
            const params: BookingQueryParams = queryParams;
            // Filter by installation's id when navigated from multi gateway
            if (params.installationId) {
              return installations.filter(
                (installation) => installation.id === Number(params.installationId)
              );
            }

            return installations;
          }),
          map((allInstallations) => {
            const bookingDevices: PartialBookingDashboardDevice[] = [];
            allInstallations.forEach((installation) => {
              if (installation.isMultiGateway) {
                // L3 issue if there's invalid simulator with empty devices property
                if (installation.devices) {
                  // Find main devices without Pro
                  installation.devices.forEach((device) => {
                    if (
                      hasMainDeviceRole(device) &&
                      HighestSubscriptionPlanPipe.getHighestDevicePlan(device) !==
                        SubscriptionPlan.Pro
                    ) {
                      bookingDevices.push(this.getPartialBookingDevice(installation, device));
                    }
                  });
                } else if (
                  installation.device &&
                  HighestSubscriptionPlanPipe.getHighestDevicePlan(installation.device) !==
                    SubscriptionPlan.Pro
                ) {
                  bookingDevices.push(
                    this.getPartialBookingDevice(installation, installation.device)
                  );
                }
              } else if (
                // Ignore installations with Pro (trial included)
                HighestSubscriptionPlanPipe.getHighestDevicePlan(installation.device) !==
                SubscriptionPlan.Pro
              ) {
                bookingDevices.push(
                  this.getPartialBookingDevice(installation, installation.device)
                );
              }
            });

            return bookingDevices;
          }),
          mergeMap((bookingDevices) =>
            combineLatest([
              of(bookingDevices),
              this.authStore.getVkorg(),
              this.authStore.getCompanyId(),
              this.routerStore.selectQueryParams$.pipe(
                map((params: BookingQueryParams): string[] => {
                  if (params.boilerSerial) {
                    return [params.boilerSerial];
                  } else {
                    return [];
                  }
                })
              ),
              this.bookingStore.selectedPlan$,
            ]).pipe(take(1))
          ),
          mergeMap(([bookingDevices, vkOrg, companyId, preselectedSerials, selectedPlan]) =>
            this.getPermamentInspectionSupportedDevices(
              companyId,
              bookingDevices.map((device) => device.vin).filter(Boolean)
            ).pipe(
              map((permamentInspection) => {
                return BookingActions.loadBookingInstallations.success({
                  bookingDevices: bookingDevices.map(
                    (device): BookingDashboardDevice => {
                      return {
                        ...device,

                        canBeBooked:
                          permamentInspection.isWhitelisted(device.vin) &&
                          device.platform !== Platform.None &&
                          this.canBeBoughtInDirectMultiGatewayPurchase(device, selectedPlan),
                        canBeBundled:
                          device.vin &&
                          BookingService.canBeUsedBundlingPackage(
                            device.platform,
                            permamentInspection.isWhitelisted(device.vin),
                            vkOrg
                          ),
                        isVoucherAvailable:
                          permamentInspection.isWhitelisted(device.vin) &&
                          (device.roles.includes(DeviceRoles.Vitodens100NA) ||
                            device.roles.includes(DeviceRoles.Vitodens200NA) ||
                            device.roles.includes(DeviceRoles.ProductVitocrossalNA)),
                      };
                    }
                  ),
                  allDevicesCount: bookingDevices.length,
                  preselectedSerials,
                });
              })
            )
          ),
          catchError((error) => {
            if (
              action.type === '[BOOKING Load booking installations] attempt' &&
              error.errorType === Api.ERROR_SNAPSHOT_NOT_FOUND
            ) {
              return of(BookingActions.loadBookingInstallations.snapshotNotFoundError());
            }

            return of(BookingActions.loadBookingInstallations.error({ error }));
          })
        )
      )
    );
  });

  retryLoadBookingInstallations$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BookingActions.loadBookingInstallations.snapshotNotFoundError),
      switchMap(() =>
        this.dashboardApiPicker
          .instance()
          .getDashboard()
          .pipe(
            map(() => BookingActions.loadBookingInstallations.retry()),
            catchError((error) => of(BookingActions.loadBookingInstallations.error({ error })))
          )
      )
    );
  });

  getSubscriptionsDetails$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(BookingActions.getSubscriptionsDetails),
      tap(() => this.uiService.blockUI()),
      // Proceed only if plan was set in booking store
      switchMap((action) =>
        zip(
          of(action),
          this.bookingStore.selectedDevices$,
          this.bookingStore.selectedPlan$.pipe(filter((plan) => !!plan))
        ).pipe(take(1))
      ),
      // Use async value from selectedInstallations to receive further installation data
      switchMap(([action, selectedInstallations, plan]) =>
        // Use actual value of selectedDevices$ to get async data from selectInstallationPackage()
        this.installationsStore.selectInstallationPackage(selectedInstallations[0]?.vin).pipe(
          take(1),
          startWith({ plan: null }),
          map((installation) => ({ action, selectedInstallations, plan, installation }))
        )
      ),
      switchMap(({ action, selectedInstallations, plan, installation }) => {
        const subscriptions = action.boilerSerial
          ? [
              {
                id: action.boilerSerial,
                plan,
              },
            ]
          : selectedInstallations.map((entry) => ({
              id: entry.vin,
              plan,
            }));
        const payload: PackageSubscriptionPayload = {
          subscriptions,
          isSimulation: true,
        };
        let apiEndpoint: Observable<
          { subscriptionId: string } | UpdateBookingSubscriptionResponse[] | SubscriptionsDetails
        >;

        if (action.update || installation?.plan === SubscriptionPlan.Plus) {
          apiEndpoint = this.monetizationApi.updateVitoguideSubscription(payload);
        } else {
          apiEndpoint = this.monetizationApi.subscribeToPackage(payload);
        }

        return apiEndpoint.pipe(
          tap(() => this.uiService.unblockUI()),
          map((subscriptionsDetails: SubscriptionsDetails) =>
            BookingActions.getSubscriptionsDetailsSuccess({ subscriptionsDetails })
          ),
          catchError((error) => {
            this.uiService.unblockUI();
            this.toastService.error({
              message: this.translateService.instant('+BOOKING.CHECKOUT.ERROR.PRICES_NOT_GET'),
            });

            return of(BookingActions.getSubscriptionsDetailsError({ error }));
          })
        );
      })
    );
  });

  subscribePackage$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(BookingActions.subscribePackage),
      tap(() => this.uiService.blockUI()),
      throttleTime(2500),
      concatLatestFrom(() => [this.bookingStore.selectedDevices$, this.bookingStore.selectedPlan$]),
      switchMap(([action, selectedInstallations, plan]) => {
        const subscriptions = selectedInstallations.map((entry) => {
          const asVoucher = {
            id: entry.vin,
            plan,
            originalBundleInvoice: action.invoiceNumber,
          };
          const asPackage = {
            id: entry.vin,
            plan,
          };

          return action.invoiceNumber ? asVoucher : asPackage;
        });

        return this.monetizationApi
          .subscribeToPackage({
            subscriptions,
          })
          .pipe(
            map(() =>
              BookingActions.subscribePackageSuccess({
                invoiceNumber: action.invoiceNumber,
              })
            ),
            catchError((error) => {
              this.uiService.unblockUI();

              return of(
                BookingActions.subscribePackageError({
                  error,
                  invoiceNumber: action.invoiceNumber,
                })
              );
            }),
            finalize(() => {
              this.uiService.unblockUI();
            })
          );
      })
    );
  });

  subscribeFlatRatePackage$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(BookingActions.subscribeFlatRatePackage),
      tap(() => this.uiService.blockUI()),
      throttleTime(2500),
      switchMap(() => {
        return this.monetizationApi
          .subscribeToPackage({
            subscriptions: [{ plan: SubscriptionPlan.Pro }],
          })
          .pipe(
            map(() => BookingActions.subscribePackageSuccess({})),
            catchError((error) => {
              this.uiService.unblockUI();

              return of(
                BookingActions.subscribePackageError({
                  error,
                })
              );
            }),
            finalize(() => {
              this.uiService.unblockUI();
            })
          );
      })
    );
  });

  subscribeCommercialPackage$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(BookingActions.subscribeCommercialPackage),
      tap(() => this.uiService.blockUI()),
      throttleTime(2500),
      concatLatestFrom(() => [this.bookingStore.selectedPlan$]),
      switchMap(([_action, plan]) => {
        return this.monetizationApi
          .subscribeToPackage({
            subscriptions: [{ plan }],
          })
          .pipe(
            map(() => BookingActions.subscribePackageSuccess({})),
            catchError((error) => {
              this.uiService.unblockUI();

              return of(
                BookingActions.subscribePackageError({
                  error,
                })
              );
            }),
            finalize(() => {
              this.uiService.unblockUI();
            })
          );
      })
    );
  });

  subscribePackageSuccess$: Observable<Action> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(BookingActions.subscribePackageSuccess),
        tap((action) => {
          let queryParams: ContingentSubscriptionsQueryParams = {
            [BOOKING_PARAMS.INVOICE_NUMBER]: action.invoiceNumber,
          };
          this.router.navigate(['/booking/summary'], {
            queryParams,
          });
          this.installationsStore.listInstallations();
          this.bookingStore.getAllVitoguideSubscriptions();
        })
      );
    },
    { dispatch: false }
  );

  subscribePackageError$: Observable<Action> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(BookingActions.subscribePackageError),
        tap((action) => {
          let queryParams: ContingentSubscriptionsQueryParams = {
            [BOOKING_PARAMS.INVOICE_NUMBER]: action.invoiceNumber,
          };
          this.router.navigate(['/booking/summary'], {
            queryParams,
          });
        })
      );
    },
    { dispatch: false }
  );

  fetchSubscriptionsPlans$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(BookingActions.fetchSubscriptionsPlans),
      switchMap(() =>
        this.monetizationApi.getVitoguideSubscriptionsPlans().pipe(
          timeout(60000),
          map((payload) =>
            BookingActions.fetchSubscriptionsPlansSuccess({ subscriptionsPlans: payload })
          ),
          catchError((error) => {
            this.toastService.error({
              message: this.translateService.instant('+BOOKING.LOADING_PLANS.ERROR'),
            });

            return of(BookingActions.fetchSubscriptionsPlansError({ error }));
          })
        )
      )
    );
  });

  getAllVitoguideSubscriptions$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(BookingActions.getVitoguideSubscriptions),
      mergeMap((action) => {
        if (action.companyId) {
          return combineLatest([
            of(action),
            this.bffInstallationsApi
              .listInstallations({ companyNumber: action.companyId })
              .pipe(map((response) => response.installations)),
            this.authStore.canManageSubscriptions$,
            this.authStore.isRsaUser(),
            this.authStore.isDemo$,
          ]).pipe(take(1));
        }

        return combineLatest([
          of(action),
          // Due to fetching all installations again there's a little window when installation list is empty,
          // checking if data exists prevents from errors
          this.installationsStore.loaded$.pipe(
            filter(Boolean),
            switchMap(() => this.installationsStore.installations$)
          ),
          this.authStore.canManageSubscriptions$,
          this.authStore.isRsaUser(),
          this.authStore.isDemo$,
          this.remoteConfigStore.isFlatRate$,
        ]).pipe(take(1));
      }),
      mergeMap(([action, installations, canManageSubscriptions, isRsa, isDemo, isFlatRate]) => {
        if (!installations?.length && !isFlatRate) {
          return of(
            BookingActions.getVitoguideSubscriptionsError({
              error: noInstallationsError,
              isResetSubcriptions: true,
            })
          );
        }

        if (canManageSubscriptions || isRsa || isDemo) {
          return this.getVitoguideSubscriptions(action.companyId, installations);
        } else {
          return this.getAvailableInstallationsPaidFeatures();
        }
      })
    );
  });

  getInstallationsPackageStatusSuccess$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(BookingActions.getVitoguideSubscriptionsSuccess),
      switchMap((action) => {
        const boilerSerials = action.subscriptions
          .filter((entry) => entry.status === Api.SubscriptionStatus.active)
          .map((entry) => entry.boilerId);

        if (boilerSerials.length) {
          return of(RemoteServiceActions.loadAllSummary({ boilerSerials }));
        }

        return of(InstallationsActions.omitLoadingRemoteServiceSummaries());
      })
    );
  });

  updateVitoguideSubscription: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(BookingActions.updateVitoguideSubscription),
      tap(() => this.uiService.blockUI()),
      concatLatestFrom(() => this.bookingStore.selectedDevices$),
      switchMap(([_action, selectedInstallations]) => {
        const subscriptions = selectedInstallations.map((entry) => ({
          id: entry.vin,
          plan: SubscriptionPlan.Pro,
        }));
        const payload: PackageSubscriptionPayload = {
          subscriptions,
        };

        return this.monetizationApi.updateVitoguideSubscription(payload).pipe(
          tap(() => this.uiService.unblockUI()),
          map(() => {
            const boilerSerial = payload.subscriptions[0]?.id;

            return BookingActions.updateVitoguideSubscriptionSuccess({ boilerSerial });
          }),
          catchError((error) => {
            this.uiService.unblockUI();

            return of(BookingActions.updateVitoguideSubscriptionError({ error }));
          })
        );
      })
    );
  });

  updateVitoguideSubscriptionSuccess$: Observable<Action> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(BookingActions.updateVitoguideSubscriptionSuccess),
        tap(() => {
          this.router.navigate(['/booking/summary']);
        })
      );
    },
    { dispatch: false }
  );

  updateVitoguideSubscriptionError$: Observable<Action> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(BookingActions.updateVitoguideSubscriptionError),
        tap(() => {
          this.router.navigate(['/booking/summary']);
        })
      );
    },
    { dispatch: false }
  );

  cancelVitoguideSubscription: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(BookingActions.cancelVitoguideSubscription),
      tap(() => this.uiService.blockUI()),
      mergeMap((action) =>
        this.monetizationApi
          .cancelVitoguideSubscription(
            action.cancelReasonCode,
            action.boilerSerial,
            action.additionalComment,
            action.companyId
          )
          .pipe(
            tap(() => this.uiService.unblockUI()),
            map(() =>
              BookingActions.cancelVitoguideSubscriptionsSuccess({
                boilerSerial: action.boilerSerial,
              })
            ),
            catchError((error) => {
              this.uiService.unblockUI();

              return of(BookingActions.cancelVitoguideSubscriptionsError({ error }));
            })
          )
      )
    );
  });

  createTrialSubscription: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(BookingActions.createTrialSubscription),
      tap(() => this.uiService.blockUI()),
      concatLatestFrom(() => this.bookingStore.selectedDevices$),
      switchMap(([_action, selectedInstallations]) => {
        const trialSubscriptions: Observable<any>[] = [];
        const bookingSubscriptions: BookingSubscription[] = [];

        selectedInstallations.forEach((installation) => {
          const bookingSubscription: BookingSubscription = {
            boilerId: installation?.vin,
            plan: SubscriptionPlan.Pro,
            trial: true,
          } as BookingSubscription;
          bookingSubscriptions.push(bookingSubscription);

          trialSubscriptions.push(
            this.monetizationApi.createTrialSubscription(installation.vin).pipe(
              map((response) => response),
              catchError((error) => of(error))
            )
          );
        });

        return forkJoin(trialSubscriptions).pipe(
          map((subscriptions) => {
            const isAllSubscriptionsFailed = subscriptions.every(
              (subscription) => subscription?.errorType
            );

            this.uiService.unblockUI();

            if (isAllSubscriptionsFailed) {
              this.toastService.error({
                message: this.translateService.instant('+BOOKING.SUBSCRIBE.ERROR'),
              });

              // TODO: Use createTrialSubscriptionError/Success for displaying proper end screen for flat rate booking
              //  (check subscribePackageSuccess$ and subscribePackageError$ for example)
              return BookingActions.createTrialSubscriptionError({
                error: subscriptions[0],
                bookingTrialStatus: BOOKING_TRIAL_STATUS.LOADED_ERROR,
              });
            } else {
              return BookingActions.createTrialSubscriptionSuccess({
                bookingTrialStatus: BOOKING_TRIAL_STATUS.LOADED_SUCCESS,
                bookingSubscriptions,
              });
            }
          })
        );
      })
    );
  });

  getTrialSubscriptions: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(BookingActions.getTrialSubscriptions),
      switchMap(() =>
        this.monetizationApi.getTrialSubscriptions().pipe(
          map((trialSubscriptions) =>
            BookingActions.getTrialSubscriptionsSuccess({ trialSubscriptions })
          ),
          catchError((error) => of(BookingActions.getTrialSubscriptionsError({ error })))
        )
      )
    );
  });

  subscribeVoucherPackage$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(BookingActions.subscribeVoucherPackage),
      tap(() => this.uiService.blockUI()),
      concatLatestFrom(() => [this.authStore.getCompanyId(), this.bookingStore.selectedDevices$]),
      switchMap(([action, companyId, selectedInstallations]) =>
        this.monetizationApi
          .subscribeVoucherPackage(
            companyId,
            action.installationId ? action.installationId : selectedInstallations[0].vin,
            action.voucher
          )
          .pipe(
            map(() => {
              this.uiService.unblockUI();

              return BookingActions.subscribePackageSuccess({});
            }),
            catchError((error: ViError) => {
              this.uiService.unblockUI();

              if (error.extendedPayload.errorType === 'VOUCHER_ALREADY_USED') {
                this.toastService.error({
                  message: this.translateService.instant('+BOOKING.VOUCHER.ERROR.ALREADY_USED'),
                });
              } else if (error.privateDetails.service === 'SAP') {
                this.toastService.error({
                  message: this.translateService.instant('+BOOKING.VOUCHER.ERROR.INVALID'),
                });
              } else if (error.statusCode === 409) {
                this.toastService.error({
                  message: this.translateService.instant('+BOOKING.VOUCHER.ERROR.CONFLICT'),
                });
              }

              return of(BookingActions.subscribeVoucherPackageError({ error }));
            })
          )
      )
    );
  });

  validateVoucherPackage$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(BookingActions.validateVoucherPackage),
      tap(() => this.uiService.blockUI()),
      concatLatestFrom(() => [this.bookingStore.selectedDevices$]),
      switchMap(([action, selectedInstallations]) =>
        this.monetizationApi
          .validateVoucherPackage(selectedInstallations[0].vin, action.voucher)
          .pipe(
            map((voucherValidationDetails) => {
              this.uiService.unblockUI();

              return BookingActions.validateVoucherPackageSuccess({ voucherValidationDetails });
            }),
            catchError((error: ViError) => {
              this.uiService.unblockUI();

              return of(BookingActions.validateVoucherPackageError({ error }));
            })
          )
      )
    );
  });

  getContingentSubscriptions$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(BookingActions.getContingentSubscriptions),
      switchMap(() =>
        this.monetizationApi.getContingentSubscriptions().pipe(
          map((contingentSubscriptions) =>
            BookingActions.getContingentSubscriptionsSuccess({ contingentSubscriptions })
          ),
          catchError((error) => of(BookingActions.getContingentSubscriptionsError({ error })))
        )
      )
    );
  });

  simulateSubscription$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(BookingActions.simulateSubscription),
      switchMap((action) => {
        const payload: PackageSubscriptionPayload = {
          companyId: action.companyId,
          isSimulation: true,
          subscriptions: [
            {
              id: action.boilerSerial,
              plan: SubscriptionPlan.Pro,
            },
          ],
        };

        return this.monetizationApi.subscribeToPackage(payload).pipe(
          map(() => BookingActions.simulateSubscriptionSuccess()),
          catchError((error) => of(BookingActions.simulateSubscriptionError({ error })))
        );
      })
    );
  });

  flatRateCancelSubscription$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BookingActions.flatRateCancelSubscription),
      tap(() => this.uiService.blockUI()),
      switchMap((action) =>
        this.monetizationApi
          .cancelFlatRateSubscription(
            action.cancelReasonCode,
            action.additionalComment,
            action.companyId
          )
          .pipe(
            map(() => {
              if (action.companyId) {
                this.bookingStore.getAllVitoguideSubscriptions(action.companyId);
              }

              return BookingActions.flatRateCancelSubscriptionSuccess();
            }),
            catchError(() => of(BookingActions.flatRateCancelSubscriptionError())),
            finalize(() => {
              this.uiService.unblockUI();
            })
          )
      )
    );
  });

  flatRateGetPackagePriceDetails$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BookingActions.flatRateGetPackagePriceDetails),
      switchMap(() =>
        this.monetizationApi.getFlatRatePriceDetails().pipe(
          map((subscriptionsDetails) =>
            BookingActions.getSubscriptionsDetailsSuccess({ subscriptionsDetails })
          ),
          catchError((error) => of(BookingActions.getSubscriptionsDetailsError(error)))
        )
      )
    );
  });

  //TODO To be refactored after BTPD-12437
  commercialGetPackagePriceDetails$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BookingActions.commercialGetPackagePriceDetails),
      switchMap((action) =>
        this.monetizationApi.getCommercialPriceDetails(action.subscriptionPlan).pipe(
          map((subscriptionsDetails) =>
            BookingActions.getSubscriptionsDetailsSuccess({ subscriptionsDetails })
          ),
          catchError((error) => of(BookingActions.getSubscriptionsDetailsError(error)))
        )
      )
    );
  });

  selectInstallationsByOnlineDevicesWithActiveTrial: Observable<Action> = createEffect(() => {
    return combineLatest([
      this.actions$.pipe(ofType(BookingActions.loadBookingInstallations.success), take(1)), // wait for store load
      this.actions$.pipe(ofType(BookingActions.selectByOnlineDevicesWithActiveTrial), take(1)),
    ]).pipe(
      switchMap(() =>
        this.bookingStore.trialSubscriptionsActiveOnlineOnly$.pipe(
          map((trialSubscriptions) =>
            trialSubscriptions.map((trialSubscription) => trialSubscription.boilerId)
          ),
          map((deviceSerials) => BookingActions.selectInstallations({ deviceSerials }))
        )
      )
    );
  });

  triggerSelectInstallations: Observable<Action> = createEffect(() => {
    return combineLatest([
      this.actions$.pipe(ofType(BookingActions.loadBookingInstallations.success), take(1)), // wait for store load
      this.actions$.pipe(ofType(BookingActions.triggerSelectInstallations), take(1)),
    ]).pipe(
      map(([_, trigger]) =>
        BookingActions.selectInstallations({ deviceSerials: trigger.deviceSerials })
      )
    );
  });

  triggerDeselectInstallations: Observable<Action> = createEffect(() => {
    return combineLatest([
      this.actions$.pipe(ofType(BookingActions.loadBookingInstallations.success), take(1)), // wait for store load
      this.actions$.pipe(ofType(BookingActions.triggerDeselectInstallations), take(1)),
    ]).pipe(
      map(([_, trigger]) =>
        BookingActions.deselectInstallations({ deviceSerials: trigger.deviceSerials })
      )
    );
  });

  resetSelectionOnPlanChange = createEffect(() => {
    return this.actions$.pipe(
      ofType(BookingActions.selectBookingPlan),
      concatLatestFrom(() => this.routerStore.selectQueryParams$),
      filter(([_action, qp]) => !qp.boilerSerial),
      map(() => BookingActions.resetUserSelection())
    );
  });

  getPermamentInspectionFeatureSupport$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(InstallationDetailsActions.getPermamentInspectionFeatureSupport),
      concatLatestFrom(() => [this.authStore.getCompanyId()]),
      switchMap(([action, companyId]) =>
        this.getPermamentInspectionSupportedDevices(companyId, [action.deviceSerial]).pipe(
          map((supportedDeviceSerials) =>
            InstallationDetailsActions.getPermamentInspectionFeatureSupportSuccess({
              isSupported: supportedDeviceSerials.isWhitelisted(action.deviceSerial),
            })
          )
        )
      )
    );
  });

  constructor(
    private actions$: Actions,
    private installationsStore: InstallationsStore,
    private bookingStore: BookingStore,
    private toastService: ViToastService,
    private translateService: TranslateService,
    private router: Router,
    private routerStore: RouterStore,
    private monetizationApi: MonetizationApi,
    private monetizationApiPicker: MonetizationApiPicker,
    private uiService: UIService,
    private bffInstallationsApi: Api.BffInstallationsApi,
    private bookingService: BookingService,
    private authStore: AuthStore,
    private remoteConfigStore: RemoteConfigStore,
    private appInsights: AppInsightsMonitoringService,
    private purchasesApi: PurchasesApi,
    private dashboardApiPicker: Api.DashboardApiPicker
  ) {}

  private getVitoguideSubscriptions(
    companyId: string,
    installations: Api.InstallationItem[]
  ): Observable<Action> {
    return this.monetizationApiPicker
      .instance()
      .getVitoguideSubscriptions(companyId)
      .pipe(
        timeout(60000),
        map((vitoguideSubscriptions) => {
          const allSubscriptionsWithDetails = this.bookingService.getFullDetailsOfSubscribedInstallations(
            vitoguideSubscriptions,
            installations
          );

          return BookingActions.getVitoguideSubscriptionsSuccess({
            subscriptions: allSubscriptionsWithDetails,
          });
        }),
        catchError((error) => {
          this.toastService.error({
            message: this.translateService.instant('+BOOKING.LOADING_PLANS.ERROR'),
          });

          return of(BookingActions.getVitoguideSubscriptionsError({ error }));
        })
      );
  }

  private getAvailableInstallationsPaidFeatures(): Observable<Action> {
    return this.purchasesApi.getAvailableInstallationsPaidFeatures().pipe(
      map((paidFeatures: SubscriptionWithPaidFeatures[]) => {
        const activeSubscriptions = this.getActiveSubscriptionFromPaidFeaturesResponse(
          paidFeatures
        );

        return BookingActions.getVitoguideSubscriptionsSuccess({
          subscriptions: activeSubscriptions as BookingSubscription[],
        });
      }),
      catchError((error) => of(BookingActions.getVitoguideSubscriptionsError({ error })))
    );
  }

  private getActiveSubscriptionFromPaidFeaturesResponse(
    paidFeaturesResponse: SubscriptionWithPaidFeatures[]
  ): InstallationPackageStatus[] {
    return paidFeaturesResponse.map((device) => ({
      plan: device.plan,
      boilerSerial: device.scope === PaidFeatureScope.device ? device.resourceId : '',
    }));
  }

  private getPermamentInspectionSupportedDevices(
    companyId: string,
    devicesIds: string[]
  ): Observable<Whitelist> {
    this.appInsights.logEvent(APP_INSIGHTS_FEATURES.GET_SUPPORTED_FEATURES, LogLevelEnum.DEBUG);

    // Endpoint returns up to 50 serials, otherwise we need to use cursor data for next data
    const maxDevicesPerRequest = 50;
    const numberOfRequests = Math.ceil(devicesIds.length / maxDevicesPerRequest);

    const requests: Observable<Api.SupportedFeatures[]>[] = [];

    for (let i = 0; i < numberOfRequests; i++) {
      const startIndex = maxDevicesPerRequest * i;
      const stopIndex = maxDevicesPerRequest * (i + 1);
      requests.push(
        this.monetizationApi
          .getSupportedFeatures(companyId, devicesIds.slice(startIndex, stopIndex))
          .pipe(
            timeout(30000),
            map(({ data }) => data)
          )
      );
    }

    return forkJoin(requests).pipe(
      map((responses) => responses.flat()),
      map((features) => {
        this.appInsights.logEvent(
          APP_INSIGHTS_FEATURES.GET_SUPPORTED_FEATURES_SUCCESS,
          LogLevelEnum.DEBUG
        );

        return features
          .map((installation) => {
            if (
              installation.features.find(
                (feature) => feature.name === SupportedFeaturesName.permamentInspection
              )
            ) {
              return installation.deviceId;
            }
          })
          .filter(Boolean);
      }),
      map((whitelist) => ({
        isWhitelisted: (deviceSerial: string) => whitelist.includes(deviceSerial),
      })),
      catchError(() => {
        this.appInsights.logEvent(
          APP_INSIGHTS_FEATURES.GET_SUPPORTED_FEATURES_ERROR,
          LogLevelEnum.ERROR
        );

        return of({
          isWhitelisted: (_deviceSerial: string) => false,
        });
      })
    );
  }

  private getPartialBookingDevice(
    installation: DashboardInstallationData,
    device: DashboardDevice | DashboardMainDevice
  ): PartialBookingDashboardDevice {
    return {
      // ...device,
      vin: device.vin,
      marketingName: device.marketingName,
      roles: device.roles,
      installationId: installation.id,
      platform: getDevicePlatformBasingOnRoles(device.roles),
      plan: HighestSubscriptionPlanPipe.getHighestDevicePlan(device),
      ownerName: installation.owner
        ? installation.owner.firstName + ' ' + installation.owner.lastName
        : '', // L3 issue
      address: installation.address,
      location: `${installation.address.street} ${installation.address.houseNumber}, ${installation.address.city} ${installation.address.zip}`,
      isMultiGateway: installation.isMultiGateway,
    };
  }

  private canBeBoughtInDirectMultiGatewayPurchase(
    device: PartialBookingDashboardDevice,
    selectedPlan: SubscriptionPlan
  ): boolean {
    if (
      device.isMultiGateway &&
      device.plan === SubscriptionPlan.Plus &&
      selectedPlan === SubscriptionPlan.Pro
    ) {
      return false;
    } else {
      return true;
    }
  }

  private getAllInstallations(): Observable<DashboardInstallationData[]> {
    const sumsName = 'notStandalone';

    return (
      this.dashboardApiPicker
        .instance()
        // Get number of all non standalone installations
        .getSumsData([
          {
            name: sumsName,
            filters: {
              [Api.DashboardFilterName.withoutStandaloneInstallations]: true,
            },
          },
        ])
        .pipe(
          mergeMap((sums) => {
            const numberOfRequests = Math.ceil(sums.data[0].value / Api.MAXIMUM_DASHBOARD_LIMIT);

            const apiRequests: Observable<DashboardInstallationData[]>[] = [];

            for (let i = 0; i < numberOfRequests; i++) {
              const offset = i * Api.MAXIMUM_DASHBOARD_LIMIT;

              apiRequests.push(
                this.dashboardApiPicker
                  .instance()
                  .getFilteredInstallationsV1(offset, Api.MAXIMUM_DASHBOARD_LIMIT, {})
                  .pipe(
                    retry(2),
                    map(({ data }) => data)
                  )
              );
            }

            return forkJoin(apiRequests);
          }),

          map((responses) => responses.flat())
        )
    );
  }
}
